import { useState } from 'react'
import { BookOpen } from 'lucide-react'
import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'

function RtpGuideContent() {
	return (
		<div className="space-y-6 text-base">
			{/* Reference image */}
			<div className="overflow-hidden rounded-lg border">
				<img
					src="/rtp-guide.webp"
					alt="RTP Guide"
					className="h-auto w-full"
					onError={(e) => {
						;(e.currentTarget as HTMLImageElement).style.display = 'none'
					}}
				/>
			</div>

			{/* Core concept */}
			<div className="space-y-2">
				<h3 className="font-semibold text-foreground">RTP คืออะไร?</h3>
				<p className="text-muted-foreground">
					RTP (Return to Player) คือเปอร์เซ็นต์ที่เกมจ่ายคืนให้ผู้เล่นโดยเฉลี่ย
					เช่น RTP 87% หมายความว่าทุก 100 บาทที่ผู้เล่นเดิมพัน
					จะได้รับเงินคืนเฉลี่ย 87 บาท ระบบเก็บไว้ 13 บาท
				</p>
			</div>

			{/* Worked example */}
			<div className="space-y-3">
				<h3 className="font-semibold text-foreground">ตัวอย่างการคำนวณ</h3>

				{/* Step 1 */}
				<div className="rounded-lg border bg-muted/30 p-4">
					<p className="mb-2 font-medium text-foreground">
						สถานการณ์: ฝาก 100 รับโบนัส 200
					</p>
					<div className="space-y-1 text-muted-foreground">
						<p>• ผู้เล่นฝาก 100 บาท</p>
						<p>• ระบบให้โบนัส 200 บาท</p>
						<p className="font-medium text-foreground">
							→ เครดิตรวมในกระเป๋า = 300 บาท (เงินจริงของผู้เล่น = 100 บาท)
						</p>
					</div>
				</div>

				{/* Step 2 */}
				<div className="rounded-lg border bg-muted/30 p-4">
					<p className="mb-2 font-medium text-foreground">
						เทิร์นโอเวอร์ 4× (Turnover 4x)
					</p>
					<div className="space-y-1 text-muted-foreground">
						<p>• ผู้เล่นต้องเดิมพันรวม 300 × 4 = 1,200 บาท</p>
						<p>• เดิมพันทั้งหมด 1,200 บาทจนกว่าจะครบเทิร์น</p>
					</div>
				</div>

				{/* Step 3 */}
				<div className="rounded-lg border bg-muted/30 p-4">
					<p className="mb-2 font-medium text-foreground">RTP 83%</p>
					<div className="space-y-1 text-muted-foreground">
						<p>
							• ระบบกิน 17% จาก 1,200 บาท = 1,200 × 17% ={' '}
							<strong className="text-foreground">204 บาท</strong>
						</p>
						<p>
							• เครดิตเหลือในกระเป๋า = 300 − 204 ={' '}
							<strong className="text-foreground">96 บาท</strong>
						</p>
					</div>
				</div>

				{/* Summary */}
				<div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950/30">
					<p className="mb-2 font-medium text-green-800 dark:text-green-200">
						สรุปผล
					</p>
					<div className="space-y-1 text-green-700 dark:text-green-300">
						<p>• ผู้เล่นฝากมาจริง 100 บาท</p>
						<p>• ผู้เล่นถอนได้ 96 บาท</p>
						<p>
							• เจ้ามือกำไร 100 − 96 ={' '}
							<strong>4 บาท (4% ของเงินฝากจริง)</strong>
						</p>
					</div>
				</div>
			</div>

			{/* Rule of thumb */}
			<div className="space-y-2">
				<h3 className="font-semibold text-foreground">
					หลักการตั้งค่า RTP ให้เหมาะสม
				</h3>
				<div className="space-y-2 text-muted-foreground">
					<p>
						เป้าหมายคือให้ผู้เล่น{' '}
						<strong className="text-foreground">
							เหลือเครดิตน้อยกว่า 90% ของยอดฝากจริง
						</strong>{' '}
						หลังเคลียร์เทิร์นครบ
					</p>
					<p>สูตรประมาณการ:</p>
					<div className="rounded-md bg-muted px-4 py-2 font-mono text-sm">
						เครดิตที่เหลือ ≈ เครดิตรวม − (เครดิตรวม × เทิร์น × (100 − RTP%))
					</div>
					<p>
						ยิ่งโบนัสสูงหรือเทิร์นน้อย ควรตั้ง RTP ต่ำลง
						เพื่อให้ระบบยังทำกำไรได้
					</p>
				</div>
			</div>

			{/* Quick reference table */}
			<div className="space-y-2">
				<h3 className="font-semibold text-foreground">ตัวอย่างอ้างอิง</h3>
				<div className="overflow-x-auto">
					<table className="w-full text-sm">
						<thead>
							<tr className="border-b text-left text-muted-foreground">
								<th className="pb-2 pr-3 font-medium">โปร</th>
								<th className="pb-2 pr-3 font-medium">ฝาก (D)</th>
								<th className="pb-2 pr-3 font-medium">เครดิตรวม (W)</th>
								<th className="pb-2 pr-3 font-medium">เทิร์น</th>
								<th className="pb-2 pr-3 font-medium">RTP</th>
								<th className="pb-2 pr-3 font-medium">เหลือ ≈</th>
								<th className="pb-2 font-medium text-green-600 dark:text-green-400">
									กำไร ≈
								</th>
							</tr>
						</thead>
						<tbody className="text-muted-foreground">
							<tr className="border-b">
								<td className="py-1.5 pr-3">สมาชิกใหม่ ฝาก x2</td>
								<td className="py-1.5 pr-3">100</td>
								<td className="py-1.5 pr-3">200</td>
								<td className="py-1.5 pr-3">5×</td>
								<td className="py-1.5 pr-3">87%</td>
								<td className="py-1.5 pr-3">~70</td>
								<td className="py-1.5 font-medium text-green-600 dark:text-green-400">
									~30 (30%)
								</td>
							</tr>
							<tr className="border-b">
								<td className="py-1.5 pr-3">ฝาก 100 รับ 300</td>
								<td className="py-1.5 pr-3">100</td>
								<td className="py-1.5 pr-3">300</td>
								<td className="py-1.5 pr-3">4×</td>
								<td className="py-1.5 pr-3">83%</td>
								<td className="py-1.5 pr-3">~96</td>
								<td className="py-1.5 font-medium text-green-600 dark:text-green-400">
									~4 (4%)
								</td>
							</tr>
							<tr className="border-b">
								<td className="py-1.5 pr-3">โบนัส 20%</td>
								<td className="py-1.5 pr-3">100</td>
								<td className="py-1.5 pr-3">120</td>
								<td className="py-1.5 pr-3">5×</td>
								<td className="py-1.5 pr-3">95%</td>
								<td className="py-1.5 pr-3">~90</td>
								<td className="py-1.5 font-medium text-green-600 dark:text-green-400">
									~10 (10%)
								</td>
							</tr>
							<tr>
								<td className="py-1.5 pr-3">ฝาก 300 รับ 500</td>
								<td className="py-1.5 pr-3">300</td>
								<td className="py-1.5 pr-3">500</td>
								<td className="py-1.5 pr-3">4×</td>
								<td className="py-1.5 pr-3">87%</td>
								<td className="py-1.5 pr-3">~240</td>
								<td className="py-1.5 font-medium text-green-600 dark:text-green-400">
									~60 (20%)
								</td>
							</tr>
						</tbody>
					</table>
				</div>
				<p className="text-sm text-muted-foreground">
					* ตัวเลขเป็นค่าเฉลี่ยระยะยาว สล็อตมีความผันผวนสูง
					ผู้เล่นแต่ละรายอาจต่างกันได้มาก
				</p>
			</div>
		</div>
	)
}

export function RtpGuideButton() {
	const [open, setOpen] = useState(false)

	return (
		<>
			<Button
				type="button"
				variant="outline"
				size="sm"
				className="h-7 gap-1.5 text-xs"
				onClick={() => setOpen(true)}
			>
				<BookOpen className="h-3.5 w-3.5" />
				วิธีการคิดค่า RTP
			</Button>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
					<DialogHeader>
						<DialogTitle>วิธีการคิดค่า RTP</DialogTitle>
					</DialogHeader>
					<RtpGuideContent />
				</DialogContent>
			</Dialog>
		</>
	)
}
